import { useSeo } from "@/lib/seo";
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';
import Reveal from '@/pages/lot-size/components/Reveal';
import ProfitLab, { type TabId } from './components/ProfitLab';
import FormulaSection from './components/FormulaSection';
import QuickAnswers from './components/QuickAnswers';
import BrokerSection from './components/BrokerSection';

const TAB_IDS: TabId[] = ['profit', 'spread', 'compound'];

function isTabId(value: string | null): value is TabId {
  return value !== null && (TAB_IDS as string[]).includes(value);
}

export default function ProfitPage() {
  useSeo({
    /* @replace:profit-title */
    title: 'Máy tính Tính toán Lợi nhuận Forex | Phân tích Lãi/Lỗ & Spread - ForexMatrix',
    /* @replace:profit-description */
    description: 'Công cụ Tính toán Lợi nhuận (Profit Calculator) của ForexMatrix giúp bạn xác định nhanh chóng số tiền lãi hoặc lỗ ròng, bóc tách chi phí Spread và mô phỏng tăng trưởng vốn dài hạn trong một giao diện duy nhất.',
    /* @replace:profit-keywords */
    keywords: 'máy tính lợi nhuận forex, tính lãi lỗ ngoại hối, tính chi phí spread, công cụ tính lợi nhuận, profit calculator forex, forex matrix',
    path: '/tinh-toan-loi-nhuan',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'profit';

  const skipScrollRef = useRef(false);

  const setActiveTab = useCallback(
    (tab: TabId) => {
      skipScrollRef.current = true;
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!tabParam) return;
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    const el = document.getElementById('profit-lab');
    if (!el) return;
    const raf = requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    return () => cancelAnimationFrame(raf);
  }, [tabParam]);

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Header */}
          <Reveal>
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                  <i className="ri-calculator-line" />
                  Forex Profit Analysis Workspace
                </span>
                <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                  Tính toán Lợi nhuận
                </h1>
                <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
                  Tính lợi nhuận giao dịch, chi phí Spread và mô phỏng tăng trưởng vốn trong một giao diện duy nhất.
                </p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-flashlight-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">Kết quả tức thì</strong> — kéo giá đóng lệnh là
                      lời hay lỗ hiện ra ngay, theo từng Pip.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-subtract-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">Đầy đủ chi phí</strong> — tự động trừ Spread,
                      Commission và Swap để ra lợi nhuận ròng chính xác.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                      <i className="ri-line-chart-line text-sm" />
                    </span>
                    <p className="text-sm leading-relaxed text-slate-600">
                      <strong className="font-semibold text-slate-800">Mô phỏng tăng trưởng</strong> — dự đoán vốn thay
                      đổi ra sao khi tái đầu tư lợi nhuận theo thời gian.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Net profit formula */}
              <div className="lg:col-span-5">
                <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-violet-50/70 to-white p-6 md:p-7">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-600 text-white">
                      <i className="ri-function-line text-lg" />
                    </span>
                    <h2 className="font-display text-lg font-bold text-slate-900">Công thức lợi nhuận ròng</h2>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    Lợi nhuận ròng không đơn giản là chênh lệch giá — bạn còn phải trừ đi mọi chi phí giao dịch.
                  </p>
                  <div className="mt-5 space-y-2.5">
                    <FormulaRow label="Lợi nhuận gộp" hint="Số Pip × Giá trị mỗi Pip × Khối lượng" accent="#10b981" />
                    <FormulaRow label="− Chi phí Spread" hint="Chênh lệch giá mua / bán" accent="#94a3b8" />
                    <FormulaRow label="− Commission" hint="Hoa hồng mỗi Lot" accent="#94a3b8" />
                    <FormulaRow label="− Swap" hint="Phí giữ lệnh qua đêm" accent="#94a3b8" />
                    <div className="flex items-center justify-between gap-3 border-t border-slate-200/80 pt-3">
                      <span className="text-sm font-bold text-slate-800">= Lợi nhuận ròng</span>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-600">
                        Kết quả thực tế
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Trading Profit Lab */}
          <div id="profit-lab" className="mt-10 scroll-mt-24">
            <Reveal delay={80}>
              <ProfitLab activeTab={activeTab} onChange={setActiveTab} />
            </Reveal>
          </div>

          {/* Content modules */}
          <div className="mt-16 space-y-12">
            <Reveal>
              <FormulaSection />
            </Reveal>
            <Reveal>
              <QuickAnswers />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />
    </div>
  );
}

function FormulaRow({ label, hint, accent }: { label: string; hint: string; accent: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-white/70 px-3.5 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-700">{label}</p>
        <p className="truncate text-[11px] text-slate-400">{hint}</p>
      </div>
      <span
        className="flex h-5 w-5 flex-none items-center justify-center rounded-full"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        <i className="ri-arrow-right-s-line text-sm" />
      </span>
    </div>
  );
}